import React, { useRef, useEffect, useContext } from 'react';
import { number, exact, array } from 'prop-types';
import { extent } from 'd3-array';
import { line } from 'd3-shape';
import { select, selectAll, selection } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { brushX } from 'd3-brush';
import { getTextWidth } from '../../utils/svg/text-utils';
import { makeYScale } from '../chart-utils/axis-functions';
import { SelectionContext } from './context';

const RangeSlider = ( { allQuarters, allChartData, currentChartData, margin, chartWidth, height } ) => {

    const { selectionQuarters, setSelectionQuarters } = useContext( SelectionContext );
    const groupRef = useRef();

    let previousSelection;
    const localMargin = {
        ...margin,
        top: 20,
        bottom: 0,
    };
    const currentChartDataValues = currentChartData
        .reduce( ( acc, row ) => [ ...acc, ...row[ 'rows' ].map( ( { value } ) => value ) ], [] )
        .filter( v => v );

    const minValue = Math.min( ...currentChartDataValues );

    // set the ranges and scale the range of the data
    const x = scaleLinear( [ 0, allQuarters.length - 1 ], [ 0, chartWidth - margin.right - margin.left ] );
    const y = makeYScale( [], height );

    y.domain( extent( [ -1, 1 ], _d => parseFloat( 0 ) ) );

    const xRangeTicks = Array.from( { length: allQuarters.length }, ( _, i ) => ( ( x.range().slice( -1 ) ) / ( allQuarters.length - 1 ) ) * i );

    selection.prototype.patternify = function ( params ) {

        const selector = params.selector;
        const elementTag = params.tag;
        const data = params.data || [ selector ];

        // Pattern in action
        let selection = this.selectAll( `.${ selector }` ).data( data, ( d, i ) => ( typeof d === 'object' && d.id ? d.id : i ) );

        selection.exit().remove();
        selection = selection.enter().append( elementTag )
            .merge( selection );
        selection.attr( 'class', selector );

        return selection;

    };

    const renderLine = () => {

        const linePath = line().defined( d => d.close !== null )
            .x( d => x( allQuarters.indexOf( d.column.quarter ) ) )
            .y( _d => y( 0 ) );

        return <path d={ linePath( currentChartData ) } stroke="#929FB2" strokeWidth={ 1 } fill="#929FB2" />;

    };

    const renderScrubber = ( override = null ) => {

        const svg = select( groupRef.current );

        const brush = brushX()
            .extent( [
                [ 0, 0 ],
                [ chartWidth - localMargin.left - localMargin.right, y( minValue ) ],
            ] )
            .on( 'brush', brushed )
            .on( 'end', brushEnded );

        const defaultSelection = [];

        if ( override !== null && override !== undefined && override.length > 0 ) {

            defaultSelection.push( ...override );

        }
        else if ( selectionQuarters && selectionQuarters.length > 0 ) {

            defaultSelection.push( ...selectionQuarters.map( s => x( allQuarters.indexOf( s ) ) ) );

        }
        else {

            defaultSelection.push( x( 0 ), x( allQuarters.length - 1 ) );

        }

        const brushContainer = svg.patternify( { tag: 'g', selector: 'brush' } );

        // create groupings for handles and add data
        const handle = brushContainer
            .patternify( {
                tag: 'g',
                selector: 'custom-handle',
                data: [ { left: true }, { left: false } ],
            } )
            .attr( 'cursor', 'ew-resize' )
            .attr( 'pointer-events', 'all' );

        const handleBorderWidth = 0.25,
            handlerFill = '#3F434D',
            handleWidth = 12,
            handleHeight = 22,
            handleDotSize = 2,
            middleHandlerStroke = '#082655',
            middleHandlerFill = '#ffffff';

        // define left and right borders of selection
        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-border',
                data: d => [ d ],
            } )
            .attr( 'width', handleBorderWidth )
            .attr( 'height', height + localMargin.top + localMargin.bottom )
            .attr( 'fill', handlerFill )
            .attr( 'stroke', handlerFill )
            .attr( 'y', 0 )
            .attr( 'x', 2 )
            .attr( 'pointer-events', 'none' );

        // create container for handles
        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-container',
                data: d => [ d ],
            } )
            .attr( 'width', handleWidth )
            .attr( 'height', handleHeight )
            .attr( 'fill', middleHandlerFill )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', handleHeight / 2 + 1 )
            .attr( 'x', -handleWidth / 2.5 )
            .attr( 'pointer-events', 'none' );

        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-line-top',
                data: d => [ d ],
            } )
            .attr( 'width', handleDotSize )
            .attr( 'height', handleDotSize )
            .attr( 'fill', middleHandlerStroke )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', 16 )
            .attr( 'x', 0 )
            .attr( 'pointer-events', 'none' );

        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-line-middle',
                data: d => [ d ],
            } )
            .attr( 'width', handleDotSize )
            .attr( 'height', handleDotSize )
            .attr( 'fill', middleHandlerStroke )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', 22 )
            .attr( 'x', 0 )
            .attr( 'pointer-events', 'none' );

        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-line-bottom',
                data: d => [ d ],
            } )
            .attr( 'width', handleDotSize )
            .attr( 'height', handleDotSize )
            .attr( 'fill', middleHandlerStroke )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', 28 )
            .attr( 'x', 0 )
            .attr( 'pointer-events', 'none' );

        // activate brush on page and select full selection
        brushContainer
            .call( brush )
            .call( brush.move, defaultSelection )
            .on( ' mousedown touchstart', brushOverlay );

        function brushed( { selection } ) {

            if ( !selection ) {

                brushContainer.call( brush.move, defaultSelection );

                return;

            }

            if ( selection ) {

                const selectionNearestTicks = [];

                for ( const s of selection ) {

                    const abs = xRangeTicks.map( xrt => Math.abs( xrt - s ) );
                    const nearestTick = abs.indexOf( Math.min( ...abs ) );

                    selectionNearestTicks.push( allQuarters[ nearestTick ] );

                }

                if ( allQuarters.indexOf( selectionNearestTicks[ 1 ] ) - allQuarters.indexOf( selectionNearestTicks[ 0 ] ) <= 1 ) {

                    selection = previousSelection;
                    brushContainer.call( brush.move, previousSelection );

                    return;

                }

                setSelectionQuarters( selectionNearestTicks );

                previousSelection = selection;

                handle
                    .attr( 'display', null )
                    .attr( 'transform', function ( d, i ) {

                        return `translate(${ selection[ i ] - 2 },${ ( height / 2 - 25 ) })`;

                    } );

                selectAll( '.custom-handle-date-text' )
                    .attr( 'x', ( _d, _i ) => {

                        const text = `${ selectionNearestTicks[ 0 ] } – ${ selectionNearestTicks[ 1 ] }`;
                        const n = getTextWidth( text, 12, 'Graphik-Bold' );

                        return ( ( chartWidth - localMargin.left - localMargin.right ) / 2 ) - ( n / 2 );

                    } )
                    .text( ( _d, i ) => {

                        if ( i === 0 ) {

                            return `${ selectionNearestTicks[ 0 ] } – ${ selectionNearestTicks[ 1 ] }`;

                        }

                        return '';

                    } );

                // change selection color and opacity
                select( '.selection' )
                    .attr( 'fill', '#011e4d' )
                    .attr( 'fill-opacity', '0.08' )
                    .attr( 'height', 40 );

                selectAll( '.custom-handle' ).raise();

            }

        }

        function brushEnded( { selection } ) {

            if ( !selection ) {

                brushContainer.call( brush.move, defaultSelection );

            }

        }

        function brushOverlay() {

            brushContainer.call( brush.move, previousSelection );

        }

        svg.select( '.domain' ).remove();
        svg.selectAll( '.tick text' ).remove();

    };

    useEffect( () => {

        renderScrubber();

    }, [ chartWidth ] );

    useEffect( () => {

        renderScrubber( [ x( 0 ), x( allQuarters.length - 1 ) ] );

    }, [ allQuarters ] );

    useEffect( () => {

        renderScrubber( selectionQuarters.map( s => x( allQuarters.indexOf( s ) ) ) );

    }, [ allQuarters, allChartData ] );

    return (
        <svg width={ chartWidth } height={ height + localMargin.top + localMargin.bottom }>

            <g ref={ groupRef } transform={ `translate(${ localMargin.left },${ localMargin.top })` }>

                <text
                    className="custom-handle-date-text"
                    x={ ( chartWidth - localMargin.left - localMargin.right ) / 2 }
                    y="-10"
                >
                    text text
                </text>

                { renderLine() }

            </g>

        </svg>
    );

};

RangeSlider.propTypes = {
    allQuarters: array,
    margin: exact( { top: number, right: number, bottom: number, left: number } ).isRequired,
    currentChartData: array,
    allChartData: array,
    chartWidth: number.isRequired,
    height: number.isRequired,
};

export default RangeSlider;
